import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./AddService.css";
import { Formik, FormikHelpers } from "formik";
import * as yup from "yup";
import { isValidPhoneNumber } from "react-phone-number-input";
import ClientSelectionFields from "./ClientSelectionFields.tsx";
import Alert from "react-bootstrap/Alert";
import ConflictObserver from "./ConflictObserver.tsx";
import { madeList } from "./utlis.ts";
import TechnicianService from "../services/technician.service";
import ServiceApiService from "../services/service.service";
import UserService from "../services/user.service";
import type { ClientSummary, Technician } from "../pages/models";

type SelectOption<T extends string | number> = {
  value: T;
  label: string;
};

type AddServiceProps = {
  show: boolean;
  onHide: () => void;
  defaultStartDate?: Date | null;
  onServiceCreated: () => void;
};

type AddServiceFormValues = {
  startDateTime: Date;
  durationHours: SelectOption<number>;
  client: ClientSummary | null;
  client_name: string;
  email: string;
  phone_number: string;
  technician: SelectOption<string> | null;
  depositAmount: string;
  mark: SelectOption<string> | null;
  model: string;
  year: string;
  note: string;
  orderNumber: string;
};

const durationOptions: SelectOption<number>[] = Array.from(
  { length: 12 },
  (_, i) => ({
  value: i + 1,
  label: `${i + 1} hour${i + 1 > 1 ? "s" : ""}`,
  }),
);

const validationSchema = yup.object().shape({
  startDateTime: yup.date().required("Date and time is required"),
  durationHours: yup.object().nullable().required("Duration is required"),
  client_name: yup
    .string()
    .required("Client name is required")
    .min(3, ({ min }) => `Client name must be at least ${min} characters`),
  email: yup
    .string()
    .email("Please enter valid e-mail")
    .required("E-mail is required"),
  phone_number: yup
    .string()
    .required("Phone number is required")
    .test("", "This not correct phone number", (value) =>
      isValidPhoneNumber(value ? value : ""),
    ),
  year: yup
    .string()
    .matches(/^\d*$/, "Year can contain digits only")
    .test("year-max", "Year must be 2100 or less", (value) =>
      !value || Number(value) <= 2100,
    ),
  orderNumber: yup
    .string()
    .matches(/^\d*$/, "Order number can contain digits only"),
});

const getErrorMessage = (error: unknown): string => {
  if (typeof error === "object" && error !== null) {
    const maybeResponse = (error as { response?: { data?: { errors?: unknown } } })
      .response;
    const errors = maybeResponse?.data?.errors;
    if (typeof errors === "string") {
      return errors;
    }

    const message = (error as { message?: unknown }).message;
    if (typeof message === "string") {
      return message;
    }
  }

  return "Unknown error";
};

const getRenderableError = (error: unknown): string | null =>
  typeof error === "string" ? error : null;

const AddService = ({
  show,
  onHide,
  defaultStartDate,
  onServiceCreated,
}: AddServiceProps) => {
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [conflictWarning, setConflictWarning] = useState<string | null>(null);
  const menuPortalTarget = typeof document !== "undefined" ? document.body : null;

  const selectStyles = {
    menuPortal: (base: Record<string, unknown>) => ({
      ...base,
      zIndex: 2000,
    }),
  };

  const getClientPhoneNumber = (client?: ClientSummary | null) =>
    client?.phoneNumber || client?.phone_number || "";

  const isSelectedClientEdited = (values: AddServiceFormValues) => {
    if (!values.client || !values.client.id) {
      return false;
    }

    return (
      values.client.clientName !== values.client_name ||
      values.client.email !== values.email ||
      getClientPhoneNumber(values.client) !== values.phone_number
    );
  };

  const getOrCreateClientId = async (values: AddServiceFormValues) => {
    const clientData = {
      clientName: values.client_name,
      email: values.email,
      phone_number: values.phone_number,
    };

    if (values.client && values.client.id) {
      if (isSelectedClientEdited(values)) {
        await UserService.updateClient(values.client.id, clientData);
      }
      return values.client.id;
    }

    const response = await UserService.postClient(clientData);
    return response.data.data.id;
  };

  useEffect(() => {
    if (show) {
      TechnicianService.getTechnicians()
        .then((res) => {
          const list = res.data.data || [];
          const filtered = list.filter((t: any) => t.supports_service !== false && t.supportsService !== false);
          setTechnicians(filtered);
        })
        .catch(() => {});
    }
  }, [show]);

  const handleSubmit = async (
    values: AddServiceFormValues,
    { setSubmitting, resetForm }: FormikHelpers<AddServiceFormValues>,
  ) => {
    try {
      const clientId = await getOrCreateClientId(values);

      const data = {
        startDateTime: values.startDateTime.toISOString(),
        durationHours: values.durationHours.value,
        clientId,
        technicianId: values.technician ? values.technician.value : null,
        depositAmount: values.depositAmount || null,
        mark: values.mark ? values.mark.value : null,
        model: values.model || null,
        year: values.year ? parseInt(values.year, 10) : null,
        orderNumber: values.orderNumber || null,
        note: values.note || null,
      };
      await ServiceApiService.createService(data);
      resetForm();
      onServiceCreated();
      onHide();
    } catch (error) {
      alert("Error creating service: " + getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const technicianOptions: SelectOption<string>[] = technicians.map((t) => ({
    value: t.id,
    label: t.name,
  }));

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      scrollable
      fullscreen="sm-down"
      dialogClassName="add-service-modal"
    >
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>New Service Appointment</Modal.Title>
      </Modal.Header>
      <Formik<AddServiceFormValues>
        initialValues={{
          startDateTime: defaultStartDate || new Date(),
          durationHours: durationOptions[0],
          client: null,
          client_name: "",
          email: "",
          phone_number: "",
          technician: null,
          depositAmount: "",
          mark: null,
          model: "",
          year: "",
          note: "",
          orderNumber: "",
        }}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit: formSubmit,
          isSubmitting,
          setFieldValue,
          setFieldTouched,
        }) => (
          <Form onSubmit={formSubmit} noValidate>
            <ConflictObserver
              startDateTime={values.startDateTime}
              durationHours={values.durationHours?.value}
              technicianId={values.technician?.value}
              type="service"
              setConflictWarning={setConflictWarning}
            />
            <Modal.Body>
              <div className="row g-3">
                <div className="col-md-6">
                  <Form.Label>Date &amp; Time *</Form.Label>
                  <DatePicker
                    selected={values.startDateTime}
                    onChange={(date: Date | null) => {
                      if (date) {
                        setFieldValue("startDateTime", date);
                      }
                    }}
                    showTimeSelect
                    timeIntervals={30}
                    dateFormat="MMMM d, yyyy h:mm aa"
                    className="form-control"
                    wrapperClassName="w-100"
                  />
                  {errors.startDateTime && touched.startDateTime && (
                    <div className="text-danger small">
                      {getRenderableError(errors.startDateTime)}
                    </div>
                  )}
                </div>
                <div className="col-md-6">
                  <Form.Label>Duration *</Form.Label>
                  <Select<SelectOption<number>, false>
                    options={durationOptions}
                    value={values.durationHours}
                    menuPortalTarget={menuPortalTarget}
                    styles={selectStyles}
                    onChange={(val) => {
                      if (val) {
                        setFieldValue("durationHours", val);
                      }
                    }}
                  />
                  {errors.durationHours && touched.durationHours && (
                    <div className="text-danger small">
                      {getRenderableError(errors.durationHours)}
                    </div>
                  )}
                </div>
                {conflictWarning && (
                  <div className="col-12 mt-2">
                    <Alert variant="danger" className="mb-0">
                      ⚠️ {conflictWarning}
                    </Alert>
                  </div>
                )}
                <div className="col-12">
                  <Form.Label>Order Number</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    name="orderNumber"
                    value={values.orderNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.orderNumber && touched.orderNumber && (
                    <div className="text-danger small">
                      {getRenderableError(errors.orderNumber)}
                    </div>
                  )}
                </div>
                <div className="col-12">
                  <ClientSelectionFields
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    showAutocomplete
                    showDetailsHeading
                    selectHeading="Client"
                    detailsHeadingSelected="Client details"
                    detailsHeadingNew="or add new one"
                    autocompletePlaceholder="Search or select client..."
                  />
                </div>
                <div className="col-md-6">
                  <Form.Label>Technician (optional)</Form.Label>
                  <Select<SelectOption<string>, false>
                    options={technicianOptions}
                    value={values.technician}
                    menuPortalTarget={menuPortalTarget}
                    styles={selectStyles}
                    onChange={(val) => setFieldValue("technician", val)}
                    isClearable
                    placeholder="Select technician..."
                  />
                </div>
                <div className="col-md-6">
                  <Form.Label>Deposit Amount (optional)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="$ Amount"
                    name="depositAmount"
                    value={values.depositAmount}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4">
                  <Form.Label>Make</Form.Label>
                  <Select<SelectOption<string>, false>
                    options={madeList}
                    value={values.mark}
                    menuPortalTarget={menuPortalTarget}
                    styles={selectStyles}
                    onChange={(val) => setFieldValue("mark", val)}
                    isClearable
                    placeholder="Select make..."
                  />
                </div>
                <div className="col-md-4">
                  <Form.Label>Model</Form.Label>
                  <Form.Control
                    type="text"
                    name="model"
                    placeholder="e.g. CBR600"
                    value={values.model}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4">
                  <Form.Label>Year</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    name="year"
                    placeholder="e.g. 2022"
                    value={values.year}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.year && touched.year && (
                    <div className="text-danger small">
                      {getRenderableError(errors.year)}
                    </div>
                  )}
                </div>
                <div className="col-12">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="note"
                    value={values.note}
                    onChange={handleChange}
                    placeholder="Any special notes..."
                  />
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={onHide}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Save Service"
                )}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default AddService;
